import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, ShoppingBag } from 'lucide-react';
import api from '../services/api';
import { formatMoney } from '../lib/money';

/**
 * Horizontally scrolling ribbon of mixed cards: top-rated stores, popular
 * products, and customer reviews. Replaces the static "Deliver to:" address
 * row that used to sit under the header on mobile.
 *
 * Designed to be marquee-style: items continuously scroll right-to-left, then
 * loop. Hovering pauses the animation. Tap a card to open the relevant page.
 */
const TrendingMarquee = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [vendorsRes] = await Promise.all([
          api.get('/vendors', { params: { limit: 20 } }),
        ]);
        if (!alive) return;
        // Pull a few menu items + reviews from the top vendors.
        const vendors = (vendorsRes.data || []).slice(0, 12);
        const productCards = [];
        const reviewCards = [];
        await Promise.all(vendors.slice(0, 6).map(async (v) => {
          try {
            const { data } = await api.get(`/vendors/${v.id}`);
            const flat = [];
            (data.menu?.sections || []).forEach((s) => {
              (s.items || []).forEach((it) => {
                if (it.image && it.price > 0) flat.push({ ...it, vendor_id: v.id, vendor_name: v.name, currency: v.currency });
              });
            });
            flat.sort(() => 0.5 - Math.random()).slice(0, 3).forEach((it) => productCards.push(it));
            // Pull a few reviews if available.
            (data.reviews || []).slice(0, 2).forEach((r) =>
              reviewCards.push({ ...r, vendor_id: v.id, vendor_name: v.name })
            );
          } catch { /* ignore single-vendor failures */ }
        }));

        const vendorCards = vendors.map((v) => ({ type: 'vendor', ...v }));
        const prodCards = productCards.map((p) => ({ type: 'product', ...p }));
        const revCards = reviewCards.map((r) => ({ type: 'review', ...r }));
        // Interleave card types so the ribbon feels varied.
        const merged = [];
        const queues = [vendorCards, prodCards, revCards];
        while (queues.some((q) => q.length)) {
          for (const q of queues) {
            if (q.length) merged.push(q.shift());
          }
        }
        if (alive) setItems(merged);
      } catch { /* keep empty */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading && items.length === 0) return null;
  if (items.length === 0) return null;

  // Duplicate the list so the marquee loops seamlessly.
  const loop = [...items, ...items];

  return (
    <div data-testid="trending-marquee" className="border-t border-slate-100 bg-white overflow-hidden">
      <div className="relative">
        <div className="trending-track flex gap-2 py-2 px-2 sm:px-4">
          {loop.map((it, i) => (
            <Card key={`${it.type}-${it.id || it.vendor_id || i}-${i}`} item={it} />
          ))}
        </div>
      </div>
      <style>{`
        .trending-track {
          width: max-content;
          animation: trending-scroll 60s linear infinite;
          will-change: transform;
        }
        .trending-track:hover { animation-play-state: paused; }
        @keyframes trending-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .trending-track { animation: none; }
        }
      `}</style>
    </div>
  );
};

const Card = ({ item }) => {
  if (item.type === 'vendor') {
    return (
      <Link to={`/vendor/${item.id}`} className="shrink-0 inline-flex items-center gap-2 bg-slate-50 hover:bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-sm rounded-full px-2 py-1.5 transition-all" data-testid={`marquee-vendor-${item.id}`}>
        {item.image
          ? <img src={item.image} alt={item.name} className="h-6 w-6 rounded-full object-cover" />
          : <div className="h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold">{(item.name || '?').charAt(0)}</div>}
        <span className="text-xs font-medium text-slate-800 max-w-[140px] truncate">{item.name}</span>
        {item.rating != null && (
          <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />{Number(item.rating).toFixed(1)}</span>
        )}
      </Link>
    );
  }
  if (item.type === 'product') {
    return (
      <Link to={`/vendor/${item.vendor_id}`} className="shrink-0 inline-flex items-center gap-2 bg-slate-50 hover:bg-white border border-slate-100 hover:border-emerald-200 hover:shadow-sm rounded-full px-2 py-1.5 transition-all" data-testid="marquee-product">
        {item.image
          ? <img src={item.image} alt={item.name} className="h-6 w-6 rounded-full object-cover" />
          : <ShoppingBag className="h-4 w-4 text-emerald-600 mx-1" />}
        <span className="text-xs font-medium text-slate-800 max-w-[140px] truncate">{item.name}</span>
        <span className="text-[10px] font-semibold text-emerald-700">{formatMoney(item.price, item.currency)}</span>
      </Link>
    );
  }
  if (item.type === 'review') {
    return (
      <Link to={`/vendor/${item.vendor_id}`} className="shrink-0 inline-flex items-center gap-2 bg-amber-50/60 hover:bg-amber-50 border border-amber-100 hover:border-amber-200 rounded-full px-2.5 py-1.5 transition-all max-w-[260px]" data-testid="marquee-review">
        <Heart className="h-3.5 w-3.5 fill-rose-500 text-rose-500 shrink-0" />
        <span className="text-xs text-slate-700 truncate italic">"{(item.text || item.body || 'Great service').slice(0, 80)}"</span>
        <span className="text-[10px] text-slate-500 shrink-0">· {item.vendor_name}</span>
      </Link>
    );
  }
  return null;
};

export default TrendingMarquee;
